// Identifiers: the API speaks 26-character Crockford base32 ULIDs, the relay frames carry
// the same 128 bits as 16-byte strings (ocs_proto::ids). Pinned by vectors/ids.json.

export const ID_LEN = 16;

const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const DECODE = new Map<string, number>();
for (let i = 0; i < ALPHABET.length; i++) DECODE.set(ALPHABET[i]!, i);
// Crockford treats I, L and O as digits; the Rust decoder does not, so neither does this one.

export class UlidError extends Error {
  constructor(value: string) {
    super(`not a ULID: ${value}`);
    this.name = "UlidError";
  }
}

export function ulidToBytes(ulid: string): Uint8Array {
  if (ulid.length !== 26) throw new UlidError(ulid);
  let v = 0n;
  for (let i = 0; i < 26; i++) {
    const d = DECODE.get(ulid[i]!.toUpperCase());
    if (d === undefined || (i === 0 && d > 7)) throw new UlidError(ulid);
    v = (v << 5n) | BigInt(d);
  }
  const out = new Uint8Array(ID_LEN);
  for (let i = ID_LEN - 1; i >= 0; i--) {
    out[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return out;
}

export function bytesToUlid(bytes: Uint8Array): string {
  if (bytes.length !== ID_LEN) throw new UlidError(`${bytes.length} bytes`);
  let v = 0n;
  for (const b of bytes) v = (v << 8n) | BigInt(b);
  const out: string[] = new Array<string>(26);
  for (let i = 25; i >= 0; i--) {
    out[i] = ALPHABET[Number(v & 0x1fn)]!;
    v >>= 5n;
  }
  return out.join("");
}

/** A fresh ULID (48-bit millisecond timestamp, 80 random bits) as bytes. */
export function newIdBytes(now = Date.now()): Uint8Array {
  const out = crypto.getRandomValues(new Uint8Array(ID_LEN));
  let t = BigInt(now);
  for (let i = 5; i >= 0; i--) {
    out[i] = Number(t & 0xffn);
    t >>= 8n;
  }
  return out;
}
